import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ig-field',
  templateUrl: './field.component.html',
  styleUrls: ['./field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldComponent),
      multi: true,
    },
  ],
})
export class FieldComponent implements AfterViewInit, ControlValueAccessor {
  @Input() mode: 'input' | 'textarea' = 'input';
  @Input() outline: 'outline' | 'fill' = 'outline';
  @Input() label: string = 'input label';
  @Input() icon: string = '';
  @Input() hint: string = '';
  @Input() cls: string = '';
  @Input() placeholder: string = '';
  @Input() isReadOnly: boolean = false;
  @Input() control: FormControl;
  @Input() maxlength = 4000;

  @ViewChild('field', { static: true }) field: MatFormField;
  @ViewChild('input', { static: false }) input: ElementRef;
  @Output() onIconClick: EventEmitter<null> = new EventEmitter();
  @Output() onInputFocus: EventEmitter<any> = new EventEmitter();
  @Output() onInputChange: EventEmitter<any> = new EventEmitter();

  value: any;

  private onChangeFn: (_: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
    if (this.control) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.control) {
      if (isDisabled) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
    }
  }

  iconClick() {
    this.onIconClick.emit();
  }

  onClick($event) {
    this.onInputFocus.emit($event);
  }

  onChange($event) {
    const val = $event.target.value;
    this.value = val;
    this.onInputChange.emit(val);
    this.onChangeFn(val);
    // this.onInputChange.emit($event);
  }

  onFocus($event) {
    if (this.outline !== 'outline') return;
    if (this.control.value) return;
    // solve the outline mode label not wide enough when focusing
    const label = this.field._elementRef.nativeElement.querySelector('label');
    const rect = label.getBoundingClientRect();

    setTimeout(() => {
      const notch = this.field._elementRef.nativeElement.querySelector(
        '.mdc-notched-outline__notch'
      );

      notch.style.width = Math.max(50, rect.width) + (this.mode === 'textarea' ? 5 : 0) + 'px';
    });
  }

  ngAfterViewInit(): void {
    this.setupKeyboardProtection();
  }

  private setupKeyboardProtection(): void {
    setTimeout(() => {
      const inputElement = this.input?.nativeElement as HTMLInputElement | HTMLTextAreaElement;
      if (!inputElement) return;

      const handleKeyDownCapture = (e: KeyboardEvent) => {
        if (e.key === ' ' || e.code === 'Space') {
          e.stopPropagation();
        }
      };

      const handleKeyPressCapture = (e: KeyboardEvent) => {
        if (e.key === ' ' || e.code === 'Space') {
          e.stopPropagation();
        }
      };

      inputElement.addEventListener('keydown', handleKeyDownCapture, true);
      inputElement.addEventListener('keypress', handleKeyPressCapture, true);

      (inputElement as any).__imgoingKeyboardCleanup = () => {
        inputElement.removeEventListener('keydown', handleKeyDownCapture, true);
        inputElement.removeEventListener('keypress', handleKeyPressCapture, true);
      };
    }, 100);
  }
}
